import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses, defaultSelectClasses } from "@/constants/ui";
import { useCalendar } from "@/contexts/calendarContext";
import useIsMobile from "@/hooks/useIsMobile";
import { localDayToUtcMidnight } from "@/lib/calendarDay";
import { cn } from "@/lib/utils";
import { Frown, SlidersHorizontal } from "lucide-react";
import DateTumbler from "../DateTumbler/DateTumbler";
import LocationTumbler from "../LocationTumbler/LocationTumbler";
import { RoomFilterSelect, AmenityFilterSelect, RoomAmenityFilterSelect } from "../CalendarFilters";
import { filterRoomsByAmenities } from "@/utils/roomAmenityFilter";
import BrickDayScheduleAlt from "../RoomSchedule/Brick/BrickDayScheduleAlt";
import BrickWeekSchedule from "../RoomSchedule/Brick/BrickWeekSchedule";

const CalendarContainer = () => {
  const {
    isLoading,
    setDate,
    availableRooms,
    setCalendarType,
    calendarType,
    selectedRooms,
    setSelectedRooms,
    selectedAmenities,
    setSelectedAmenities,
    availableAmenities,
    selectedRoomAmenities,
    setSelectedRoomAmenities,
    availableRoomAmenities,
  } = useCalendar();
  const isMobile = useIsMobile();

  const numberOfAvailableRooms = availableRooms?.length ?? 0;

  // When room-amenity filters are active, the room picker only offers matching rooms.
  const amenityFilteredRooms = filterRoomsByAmenities(availableRooms ?? [], selectedRoomAmenities);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid flex items-center justify-center">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Just a moment...</span>
      </div>
    );
  }

  const handleSetToday = () => {
    // Anchor "today" to UTC-midnight of the local calendar day so the UTC-anchored grid opens on
    // the day the viewer calls today, not the UTC day of "now" (off-by-one early morning in BST).
    setDate(localDayToUtcMidnight(new Date()));
  };

  const filterPopover = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={isLoading || numberOfAvailableRooms === 0}
          className={defaultButtonClasses}
          id="filter-flyout-trigger"
          aria-label="Filter calendar display"
        >
          <SlidersHorizontal />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-4" align="start" sideOffset={8}>
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="font-semibold text-sm mb-2">Filter Rooms</h3>
            <RoomFilterSelect
              availableRooms={amenityFilteredRooms}
              selectedRooms={selectedRooms}
              onSelectionChange={setSelectedRooms}
              idPrefix="calendar-container"
            />
          </div>

          {availableRoomAmenities.length > 0 && (
            <div>
              <h3 className="font-semibold text-sm mb-2">Rooms with amenities</h3>
              <RoomAmenityFilterSelect
                availableRoomAmenities={availableRoomAmenities}
                selectedRoomAmenities={selectedRoomAmenities}
                onSelectionChange={setSelectedRoomAmenities}
                idPrefix="calendar-container"
              />
            </div>
          )}

          {availableAmenities.length > 0 && (
            <div>
              <h3 className="font-semibold text-sm mb-2">Filter Amenities</h3>
              <AmenityFilterSelect
                availableAmenities={availableAmenities}
                selectedAmenities={selectedAmenities}
                onSelectionChange={setSelectedAmenities}
                idPrefix="calendar-container"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  const renderEmpty = () => {
    return (
      <Empty className="bg-gray-100 dark:bg-slate-900 dark:border-slate-900 border-2 border-gray-100 rounded-lg p-6 border-solid mt-8 mb-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Frown />
          </EmptyMedia>
          <EmptyTitle className="tpr-font text-2xl">No rooms at this location yet.</EmptyTitle>
        </EmptyHeader>
        <EmptyDescription className="max-w-2/3 mx-auto text-center">
          We haven't added any rooms to this location yet. Please contact Head Office if you think this is an error.
        </EmptyDescription>
      </Empty>
    );
  };

  const renderNonMobileRoomSchedules = () => {
    return (
      <div data-testid="desktop-scheduler">
        <div className="p-2 w-full gap-2">
          <div className="flex flex-col justify-between">
            <div className="flex flex-row justify-between gap-4">
              <div className="flex-grow">
                <LocationTumbler disabled={isLoading} />
              </div>
              <div className="flex-grow">
                <DateTumbler disabled={isLoading} hideNavButtons={calendarType === "week"} />
              </div>
              <div>
                <Select
                  disabled={isLoading || numberOfAvailableRooms === 0}
                  value={calendarType}
                  onValueChange={(value) => {
                    setCalendarType(value as "day" | "week");
                    // UTC-midnight of the local day (see handleSetToday) so the grid lands on today.
                    setDate(localDayToUtcMidnight(new Date())); // reset to today when changing calendar types for better UX
                  }}
                >
                  <SelectTrigger
                    data-testid="calendar-type-select"
                    className={cn(
                      defaultSelectClasses,
                      "justify-center items-center text-lg [&_span]:text-black dark:[&_span]:text-white [&_span]:text-base [&_span]:dark:white [&_span]:px-4",
                    )}
                  >
                    <div className="flex items-center gap-2 text-lg">
                      <SelectValue placeholder={calendarType} aria-label={calendarType} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="day" title="Day view">
                        <div className="flex flex-row w-full">
                          <div className="pr-2">Day</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="week" title="Week view">
                        <div className="flex flex-row w-full">
                          <div className="pr-2">Week</div>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Button
                  variant="default"
                  disabled={isLoading || numberOfAvailableRooms === 0}
                  onClick={handleSetToday}
                  className={defaultButtonClasses}
                >
                  Today
                </Button>
              </div>
              <div>{filterPopover}</div>
            </div>
          </div>
        </div>

        {/* here is where we render based on which calendar type is selected */}
        <div data-testid="desktop-scheduler-scheduler">
          {numberOfAvailableRooms === 0 ? (
            renderEmpty()
          ) : (
            <>
              {calendarType === "day" && <BrickDayScheduleAlt />}
              {calendarType === "week" && <BrickWeekSchedule />}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderMobileRoomSchedules = () => {
    return (
      <div data-testid="mobile-scheduler">
        <div className="p-2 w-full gap-2">
          <div className="flex flex-col gap-2 md:hidden">
            <LocationTumbler disabled={isLoading} />
            <div className="flex flex-row gap-2 items-center">
              <div className="flex-1 min-w-0">
                <DateTumbler disabled={isLoading} />
              </div>
              <Button variant="default" disabled={isLoading || numberOfAvailableRooms === 0} onClick={handleSetToday}>
                Today
              </Button>
              {filterPopover}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 p-2">
          {numberOfAvailableRooms === 0 ? renderEmpty() : <BrickDayScheduleAlt />}
        </div>
      </div>
    );
  };

  return <div>{isMobile ? renderMobileRoomSchedules() : renderNonMobileRoomSchedules()}</div>;
};

export default CalendarContainer;
